import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DateFormat } from '@vista/i18n';
import { getLoyaltyItemById, getLoyaltyRelatedFilmById, getLoyaltyRelatedShowtimeById, getLoyaltyRelatedTicketTypeById, getLoyaltySiteById } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { useI18n } from '../../../context/occ/hooks/use-i18n';
import { getGroupedLines } from '../../../utils/get-grouped-lines';
import { TransactionTotalsMode } from '../types/config';
import { MemberTransactionListContextInstance } from '../types/context';
const b = bemClassBuilder('v-member-transaction-list-table-row');
export const MemberTransactionListTableRow = observer(props => {
    const i18n = useI18n();
    const { transaction } = props;
    const { config } = useContext(MemberTransactionListContextInstance);
    const balanceAdjustment = transaction.balanceAdjustments.reduce((acc, adjustment) => acc + adjustment.units, 0);
    const formattedPrice = getFormattedPrice(transaction.totalPriceIncludingTax, i18n);
    const showtime = getFirstShowtime(props);
    const site = showtime && getLoyaltySiteById(showtime.siteId);
    const formattedDate = transaction.createdAt
        ? i18n.date(transaction.createdAt, {
            format: DateFormat.DateShort,
            ianaTimeZoneName: site ? site.ianaTimeZoneName : undefined
        })
        : undefined;
    return (_jsxs("tr", Object.assign({ className: b.block() }, { children: [_jsx("td", Object.assign({ className: b.element('date') }, { children: formattedDate })), _jsx("td", Object.assign({ className: b.element('details') }, { children: _jsxs("div", Object.assign({ className: b.element('details-wrapper') }, { children: [formattedDate && _jsx("span", Object.assign({ className: b.element('details-date') }, { children: formattedDate })), showtime && _jsx("span", Object.assign({ className: b.element('details-film') }, { children: getFilmTitle(showtime, i18n) })), _jsx("ul", Object.assign({ className: b.element('details-line-items') }, { children: getTransactionLineDescriptions(props, i18n).map((item, index) => (_jsx("li", { children: item }, index))) })), showtime && _jsx("span", Object.assign({ className: b.element('details_site') }, { children: getSiteName(showtime, i18n) }))] })) })), _jsx("td", Object.assign({ className: b.element('totals') }, { children: config.transactionTotalsMode === TransactionTotalsMode.Price ? (_jsx("span", Object.assign({ className: b.element('price') }, { children: formattedPrice }))) : (_jsx("span", Object.assign({ className: b.element('balance-adjustment', { negative: balanceAdjustment < 0 }) }, { children: getFormattedBalanceAdjustment(balanceAdjustment, i18n) }))) }))] })));
});
MemberTransactionListTableRow.displayName = 'MemberTransactionListTableRow';
const getFirstShowtime = (props) => {
    const { transaction } = props;
    if (transaction.showtimes && transaction.showtimes.length > 0) {
        const showtimeId = transaction.showtimes[0].showtimeId;
        if (!showtimeId)
            return undefined;
        return getLoyaltyRelatedShowtimeById(showtimeId);
    }
    return undefined;
};
const getFormattedBalanceAdjustment = (balanceAdjustment, i18n) => {
    const formattedAdjustment = i18n.number(balanceAdjustment);
    return balanceAdjustment > 0 ? `+${formattedAdjustment}` : formattedAdjustment;
};
const getFormattedPrice = (price, i18n) => {
    if (isNaN(price))
        return undefined;
    return i18n.currency(price);
};
const getFilmTitle = (showtime, i18n) => {
    if (!showtime.filmId)
        return undefined;
    const film = getLoyaltyRelatedFilmById(showtime.filmId);
    return i18n.translatable(film === null || film === void 0 ? void 0 : film.title);
};
const getSiteName = (showtime, i18n) => {
    if (!showtime.siteId)
        return undefined;
    const site = getLoyaltySiteById(showtime.siteId);
    return i18n.translatable(site === null || site === void 0 ? void 0 : site.name);
};
const getTransactionLineDescriptions = (props, i18n) => {
    const { transaction } = props;
    const firstShowtime = transaction.showtimes[0];
    const groupedTickets = firstShowtime ? getGroupedTicketsDescriptionsForShowtime(firstShowtime, i18n) : [];
    const groupedItems = getGroupedItemDescriptionsForTransaction(transaction, i18n);
    return groupedTickets.concat(groupedItems);
};
const getItemDescription = (itemLine, i18n) => {
    const item = getLoyaltyItemById(itemLine.line.itemId);
    return item ? `${itemLine.quantity}x ${i18n.translatable(item.name)}` : '';
};
const getTicketDescription = (ticketLine, i18n) => {
    const ticket = getLoyaltyRelatedTicketTypeById(ticketLine.line.ticketTypeId);
    return ticket ? `${ticketLine.quantity}x ${i18n.translatable(ticket.description)}` : '';
};
const getGroupedTicketsDescriptionsForShowtime = (showtime, i18n) => {
    const groupedTicketLines = getGroupedLines(showtime.tickets, 'ticketTypeId');
    return groupedTicketLines.map(ticket => getTicketDescription(ticket, i18n));
};
const getGroupedItemDescriptionsForTransaction = (transaction, i18n) => {
    const groupedItemLines = getGroupedLines(transaction.items, 'itemId');
    return groupedItemLines.map(item => getItemDescription(item, i18n));
};
